(() => {
  if (customElements.get('audio-note')) return;

  /* ---------- Split-Test Position der Sprachnotiz ----------
     Die Zuteilung setzt das Inline-Skript im Snippet sofort beim Rendern (html[data-audio-split]).
     Gespeichert und gemessen wird nur mit Statistik-Einwilligung. Ohne Einwilligung gilt die
     Zuteilung nur fuer diesen Seitenaufruf und es geht nichts raus. */
  const SPLIT_KEY = 'sniffys-audio-split-v1';
  const TEST_ID = 'audio-position-v1';
  const root = document.documentElement;
  const hasSplit = () => document.querySelector('.audio-note[data-audio-slot]') !== null;
  const variant = () => root.dataset.audioSplit;
  let persisted = false;

  const consentGiven = () => {
    try {
      return Boolean(window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.());
    } catch (e) {
      return false;
    }
  };

  const endpoint = () => document.querySelector('.audio-note[data-audio-endpoint]')?.dataset.audioEndpoint;

  const sessionId = () => {
    try {
      let id = sessionStorage.getItem('sniffys-audio-split-session');
      if (!id) {
        id = crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2);
        sessionStorage.setItem('sniffys-audio-split-session', id);
      }
      return id;
    } catch (e) {
      return null;
    }
  };

  const track = (event, extra = {}) => {
    if (!hasSplit() || !persisted) return;
    const url = endpoint();
    if (!url) return;
    const product = document.querySelector('.audio-note')?.dataset.product;
    const body = JSON.stringify({ test: TEST_ID, variant: variant(), event, product, session: sessionId(), page: location.pathname, theme: window.Shopify?.theme?.role, ...extra });
    try {
      if (!navigator.sendBeacon?.(url, new Blob([body], { type: 'text/plain' }))) {
        fetch(url, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'text/plain' } });
      }
    } catch (e) {}
  };

  /* Variante am Warenkorb, damit sie in der Bestellung landet. Einmal pro Sitzung pruefen,
     weil nach einer Bestellung oder nach Ablauf ein neuer Warenkorb ohne Attribut entsteht. */
  /* Beide Split-Skripte schreiben in denselben Warenkorb, deshalb nacheinander ueber eine gemeinsame Kette */
  const tagCart = (name, value) => {
    const flag = `sniffys-cart-tag:${name}:${value}`;
    try {
      if (sessionStorage.getItem(flag)) return;
    } catch (e) {}
    const run = async () => {
      const routes = window.Shopify?.routes?.root || '/';
      let cart = await fetch(`${routes}cart.js`).then((r) => r.json());
      if (cart.attributes?.[name] !== value) {
        cart = await fetch(`${routes}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { [name]: value } }),
        }).then((r) => r.json());
      }
      if (cart.attributes?.[name] === value) sessionStorage.setItem(flag, '1');
    };
    window.__sniffysCartTags = (window.__sniffysCartTags || Promise.resolve()).then(run).catch(() => {});
  };

  const persist = () => {
    /* ?audio_split=a|b|c erzwingt eine Position zum Testen, ohne Speichern und ohne Messung */
    if ('audioSplitForced' in root.dataset) return;
    if (persisted || !hasSplit() || !variant() || !consentGiven()) return;
    persisted = true;
    try {
      localStorage.setItem(SPLIT_KEY, variant());
    } catch (e) {}
    tagCart('_audio_split', `${TEST_ID}:${variant()}`);
    track('product_view');
  };

  const clearOnWithdraw = () => {
    if (consentGiven()) return;
    try {
      localStorage.removeItem(SPLIT_KEY);
    } catch (e) {}
  };

  document.addEventListener('visitorConsentCollected', () => {
    clearOnWithdraw();
    persist();
  });

  const whenPrivacyReady = (tries = 0) => {
    if (window.Shopify?.customerPrivacy) return persist();
    if (tries < 40) setTimeout(() => whenPrivacyReady(tries + 1), 250);
  };
  whenPrivacyReady();

  /* Warenkorb-Hinzufuegen erkennen, egal ueber welches Formular oder Bundle */
  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    try {
      const url = typeof input === 'string' ? input : input?.url;
      if (url && /\/cart\/add(\.js)?(\?|$)/.test(url)) track('add_to_cart');
    } catch (e) {}
    return nativeFetch.apply(this, arguments);
  };

  document.addEventListener('click', (e) => {
    const link = e.target.closest?.('[data-audio-event]');
    if (link) track(link.dataset.audioEvent);
  });

  /* ---------- Player ---------- */
  const format = (s) => {
    if (!Number.isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };

  class AudioNote extends HTMLElement {
    connectedCallback() {
      this.audio = this.querySelector('audio');
      this.toggle = this.querySelector('.audio-note__toggle');
      this.wave = this.querySelector('.audio-note__wave');
      this.bars = [...this.wave.children];
      this.current = this.querySelector('[data-current]');
      this.duration = this.querySelector('[data-duration]');
      this.milestones = new Set();

      this.toggle.addEventListener('click', () => (this.audio.paused ? this.play() : this.audio.pause()));
      this.audio.addEventListener('loadedmetadata', () => this.render());
      this.audio.addEventListener('durationchange', () => this.render());
      this.audio.addEventListener('timeupdate', () => {
        this.render();
        this.trackProgress();
      });
      this.audio.addEventListener('play', () => this.setPlaying(true));
      this.audio.addEventListener('pause', () => this.setPlaying(false));
      this.audio.addEventListener('ended', () => {
        this.audio.currentTime = 0;
        this.render();
      });

      this.wave.addEventListener('pointerdown', (e) => this.seek(e));
      this.wave.addEventListener('keydown', (e) => {
        if (!this.audio.duration) return;
        const step = { ArrowRight: 5, ArrowLeft: -5 }[e.key];
        if (!step) return;
        e.preventDefault();
        this.audio.currentTime = Math.min(this.audio.duration, Math.max(0, this.audio.currentTime + step));
      });

      if (this.audio.readyState >= 1) this.render();
    }

    play() {
      document.querySelectorAll('audio-note').forEach((note) => {
        if (note !== this) note.audio?.pause();
      });
      this.audio.play();
    }

    seek(e) {
      if (!this.audio.duration) return;
      const rect = this.wave.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      this.audio.currentTime = ratio * this.audio.duration;
      if (this.audio.paused) this.play();
    }

    setPlaying(playing) {
      this.toggle.classList.toggle('is-playing', playing);
      this.toggle.setAttribute('aria-label', playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay);
      if (playing && !this.milestones.has('start')) {
        this.milestones.add('start');
        track('audio_play');
      }
    }

    trackProgress() {
      const { currentTime, duration } = this.audio;
      if (!duration) return;
      const ratio = currentTime / duration;
      [50, 90].forEach((p) => {
        if (ratio * 100 >= p && !this.milestones.has(p)) {
          this.milestones.add(p);
          track(`audio_${p}`);
        }
      });
    }

    render() {
      const { currentTime, duration } = this.audio;
      const ratio = duration ? currentTime / duration : 0;
      const played = Math.round(ratio * this.bars.length);
      this.bars.forEach((bar, i) => bar.classList.toggle('is-played', i < played));
      this.wave.setAttribute('aria-valuenow', Math.round(ratio * 100));
      this.current.textContent = format(currentTime);
      this.duration.textContent = format(duration);
    }
  }

  customElements.define('audio-note', AudioNote);
})();
